/**
 * The layer ladder (§ 204), as data.
 *
 * Exists for the same reason `palette.ts` does: `tailwind.config.js` cannot be
 * imported into app code (it `require`s build tooling), so a page that wants to
 * SHOW the ladder — the showcase's Elevation section — needs the values as a
 * module. `__tests__/layerLadder.test.tsx` pins this object to the config's
 * `zIndex` extension in both directions, the `motion.ts` arrangement, so the two
 * cannot drift.
 *
 * The values were the ones the layers always rendered — the scale NAMED them
 * (2026-09-03) rather than moving anything. **§ 283 (2026-09-13) moved them for the
 * first time**, for the reason in `LEGACY_MODAL_ROOT_Z` below. Numeric z
 * (tw-z-10/20/30) remains legal for LOCAL tiers inside one component's own stacking
 * context; only the global layers take a name.
 */
export const LAYERS = {
  /** Scrims and floating surfaces: Dialog, SidePanel, DropdownButton's inline menu, the anchored Tooltip. */
  overlay: 5050,
  /**
   * § 304 — a PORTALLED anchored surface: Dropdown's listbox, Combobox's list,
   * Popover, DropdownButton's menu, all rendered into `document.body` at a fixed
   * position. One rung above `overlay`, because every one of them can be opened from
   * inside a Dialog or a SidePanel and must paint over the panel that opened it;
   * under `toast`, because a "Saved" raised while a menu is open still has to be
   * seen. Dropdown's portal sat at `tooltip` (9999) before there was a rung for it.
   */
  popover: 5100,
  /** Above the dialog it was raised from — "Saved" behind the modal that saved is no toast at all. */
  toast: 6000,
  /** Pointer-attached, on top of everything — the portal Tooltip. */
  tooltip: 9999,
} as const;

/**
 * The legacy `#modal-root` (`App.tsx`) is the one global stacking context NOT on this
 * ladder, and it is why the numbers above are what they are.
 *
 * It carries `z-5000`. While the ladder sat at 50 / 2040 / 9999, **every DS overlay
 * opened from inside a `Components/Common/Modal/ModalContainer` was painted behind
 * it** — present in the DOM, invisible on screen, and unreachable. Reported on the
 * Recategorize wizard's "Change target category?" confirm, which mounts correctly
 * (backdrop, panel and both buttons all in the document) and simply never appeared.
 * Toasts raised from inside such a modal were lost the same way, which contradicted
 * `ToastRegion`'s own rule that a toast must clear the dialog that raised it.
 *
 * Lowering `#modal-root` instead does not work: the page behind it reaches z-2030
 * (`#sidepanel-portal`, `Layout.tsx`) and z-1001 (DeltaTable's sticky `thead`), so a
 * legacy modal placed under the overlay tier would let a sticky table header paint
 * over it.
 *
 * So the ladder moved up and the legacy root stayed put. Every rung above must keep
 * clearing this value; `layerLadder.test.tsx` asserts it.
 */
export const LEGACY_MODAL_ROOT_Z = 5000;

export type LayerName = keyof typeof LAYERS;

/** Render order, bottom to top — what the showcase draws. */
export const LAYER_ORDER: LayerName[] = ['overlay', 'popover', 'toast', 'tooltip'];
